<script lang="ts" setup>
import ThreadsNav from "@/components/mobile/ThreadsNav.vue";
import StoryView from "@/components/mobile/story/story-chapter/StoryView.vue";
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStory } from "@/stores/story";
import { useMate } from "@/stores/mate";
import { useFollow } from "@/stores/follow";
import { Cell, Popup } from "vant";
import ScreenshotSharePopup, {
  ScreenshotSharePopupExpose,
} from "@/components/mobile/popup/ScreenshotSharePopup.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import { useReply } from "@/stores/reply";
import i18n from "@/locales";
import { GAL_GAME_URL_PREFIX } from "@/stores/storyGameEditor";
import { Mate, Story } from "@/types/entity";
// State

const replyStore = useReply();
const currentStory = ref<Story | null>(null);
const storyList = ref<Story[]>([]);
const sortedStoryList = computed(() => {
  return [...storyList.value].sort((a, b) => b.order! - a.order!);
});
const prevStoryUuid = computed<string | null>(() => {
  const idx = sortedStoryList.value.findIndex(
    (story) => story.uuid === currentStory.value?.uuid
  );
  if (idx === -1 || idx === 0) {
    return null;
  }
  return sortedStoryList.value[idx - 1].uuid;
});
const nextStoryUuid = computed<string | null>(() => {
  const idx = sortedStoryList.value.findIndex(
    (story) => story.uuid === currentStory.value?.uuid
  );
  if (idx === -1 || idx === sortedStoryList.value.length - 1) {
    return null;
  }
  return sortedStoryList.value[idx + 1].uuid;
});
const author = ref<Mate | null>(null);
const loading = ref(true);
const followId = ref<number | null>(null);
const mateStore = useMate();
const route = useRoute();
const router = useRouter();
const storyStore = useStory();
const followStore = useFollow();
const screenshotSharePopupRef = ref<ScreenshotSharePopupExpose | null>(null);

const showMenu = ref(false);
// Lifecycle
const prepareData = async () => {
  loading.value = true;
  try {
    const uuid = route.params.uuid as string;
    currentStory.value = await storyStore.getStoryItem(uuid);
    queryStoryList();
    queryAuthor();
  } finally {
    loading.value = false;
  }
};

const storyListLoaded = ref(false);
const queryStoryList = async () => {
  try {
    storyList.value = await storyStore.getStoryListByStoryBook(
      currentStory.value!.storyBook!,
      currentStory.value!.mate.uuid !== mateStore.currentMate?.uuid,
      currentStory.value!.mate.id
    );
    storyListLoaded.value = true;
  } catch (e) {
    console.log(e);
  }
};

const queryAuthor = async () => {
  try {
    [author.value] = await Promise.all([
      mateStore.getOrFetchMate(currentStory.value!.mate.uuid),
      replyStore.createOnePv(currentStory.value!.reply.id!),
    ]);
    if (mateStore.currentMate) {
      followId.value = await followStore.isFollowing(author.value!.id!);
    }
  } catch (e) {
    console.log(e);
  }
};

onBeforeMount(() => {
  prepareData();
});

const onGoTargetStory = async (uuid: string) => {
  await router.replace({ params: { uuid } });
  showMenu.value = false;
  await prepareData();
};

const clickOpenGame = () => {
  window.open(
    GAL_GAME_URL_PREFIX +
      `?uuid=${currentStory.value?.storyGameUuid}&nodeId=${currentStory.value?.storyGameStoryNodeId}&type=story`
  );
};
</script>
<template>
  <div class="h-full grid grid-rows-[auto_1fr_auto] relative text-base">
    <threads-nav :enable-switch-mate="false" :show-back="true">
      <template #center>
        <div class="font-bold">
          {{ currentStory?.title ?? "" }}
        </div>
      </template>
      <template #right>
        <!-- <story-share-button
          v-if="enableStoryShareBtn"
          v-model:show="showStoryShare"
          :story="currentStory!"
        /> -->
        <button
          :disabled="!storyListLoaded"
          class="btn btn-sm btn-ghost"
          @click="showMenu = true"
        >
          <book-icon />
        </button>
        <share
          :share-id="currentStory?.id!"
          :share-type="'story'"
          :story-game-id="currentStory?.storyGameId ?? undefined"
          only-icon
        />
      </template>
    </threads-nav>
    <div class="w-full h-full overflow-y-auto flex flex-col">
      <div
        v-if="loading"
        class="w-full h-full flex items-center justify-center"
      >
        <span class="loading"></span>
      </div>
      <div v-else class="overflow-y-auto p-4 min-h-full">
        <div class="border-b py-2 mb-4 flex flex-col gap-2">
          <div
            class="text-xl font-bold w-full overflow-hidden whitespace-pre-line"
          >
            {{ currentStory?.title }}
          </div>
          <div
            v-if="currentStory?.storyBook"
            class="flex gap-1 items-center overflow-hidden"
          >
            <div
              class="btn btn-xs cursor-pointer flex"
              @click="
                router.push({
                  name: 'storyBookHome',
                  params: {
                    uuid: currentStory?.storyBook?.uuid,
                  },
                })
              "
            >
              <i class="iconify mingcute--book-2-line" />
              <span class="line-clamp-1 flex-1">
                {{ currentStory?.storyBook?.title }}
              </span>
            </div>
          </div>

          <div v-if="author" class="flex gap-1 items-center">
            <div
              class="btn btn-xs"
              @click="
                router.push({
                  name: 'mateHome',
                  params: {
                    uuid: author?.uuid,
                  },
                })
              "
            >
              <user-avatar
                :avatar-url="author?.mediaUrl"
                :mate="author"
                class="w-4"
              />
              <span>{{ author?.name }}</span>
            </div>
          </div>

          <div
            v-if="
              currentStory?.videoMedia || currentStory?.storyGameStoryNodeId
            "
            class="flex gap-1 items-center"
          >
            <div class="text-xs text-base-content/50">
              {{ $t("story.readMethod") }}
            </div>
            <button
              v-if="currentStory?.videoMedia"
              class="btn btn-xs text-xs gap-1"
              @click="
                screenshotSharePopupRef?.startGenerateScreenshot(
                  currentStory?.videoMedia
                )
              "
            >
              <i class="iconfont icon-play"></i>
              <span>{{ $t(`story.viewVideo`) }}</span>
            </button>

            <button
              v-if="currentStory?.storyGameStoryNodeId"
              class="btn btn-xs text-xs gap-1"
              @click="clickOpenGame"
            >
              <i class="iconfont icon-play"></i>
              <span>{{ $t(`story.immersiveMode`) }}</span>
            </button>
          </div>
        </div>
        <story-view :story="currentStory!" class="text-base" />
        <div
          v-if="prevStoryUuid || nextStoryUuid"
          class="flex justify-between items-center w-full gap-2"
        >
          <div class="flex-1">
            <button
              :disabled="!prevStoryUuid"
              class="btn btn-neutral btn-sm w-full"
              @click="onGoTargetStory(prevStoryUuid!)"
            >
              {{
                prevStoryUuid
                  ? i18n.global.t("Buttons.previousChapter")
                  : i18n.global.t("Buttons.firstChapter")
              }}
            </button>
          </div>
          <div class="flex-1">
            <button
              :disabled="!nextStoryUuid"
              class="btn btn-neutral btn-sm w-full"
              @click="onGoTargetStory(nextStoryUuid!)"
            >
              {{
                nextStoryUuid
                  ? i18n.global.t("Buttons.nextChapter")
                  : i18n.global.t("Buttons.lastChapter")
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <popup v-model:show="showMenu" class="h-[80%]" position="bottom" round>
      <div class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
        <div class="p-4 flex flex-col gap-4 border-b">
          <div
            class="flex gap-2 cursor-pointer"
            @click="
              router.push({
                name: 'storyBookHome',
                params: { uuid: currentStory?.storyBook?.uuid },
              })
            "
          >
            <div>
              <div class="text-base font-bold">
                {{ currentStory?.storyBook?.title }}
              </div>
              <div class="text-sm text-base-content/50">
                {{ author?.name }}
              </div>
            </div>
          </div>
          <div class="text-xs text-base-content/50">
            {{ $t("story.ongoing") }}
            {{ storyList.length }}
            {{ $t("story.ongoingTwo") }}
          </div>
        </div>
        <div class="overflow-y-auto">
          <cell
            v-for="(item, idx) in sortedStoryList"
            :key="item.id"
            :class="{
              'bg-primary text-primary': item.uuid === currentStory?.uuid,
            }"
            :title="`${idx + 1}. ${item.title}`"
            class="bg-transparent"
            is-link
            @click="onGoTargetStory(item.uuid)"
          />
        </div>
      </div>
    </popup>
    <screenshot-share-popup
      ref="screenshotSharePopupRef"
      :on-screenshot-generate="() => storyStore.storyScreenshot(currentStory!.uuid)"
    />
  </div>
</template>
